// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React, { useState } from 'react';
import {
  AppIcon,
  Checkbox,
  CountrySelect,
  DateTimeInput,
  DEFAULT_PHONE_COUNTRY,
  Dropdown,
  InputLabel,
  PhoneFlagDropdown,
  Radio,
  TextArea,
  TextInput,
  Tooltip,
  type DropdownOption,
} from '@loa/ui-components';
import type { AnswerMap, ClientOption, WizardField } from './types';
import { optionLabel, optionValue } from './wizardLogic';
import { currencySymbol } from './currency';
import { useGroupedAmount } from './useGroupedAmount';
import { useMonthYearInput } from './useMonthYearInput';
export type { ClientOption } from './types';

/**
 * When true, every input placeholder is suppressed — the client Fact Find wizard
 * turns this on so the form renders no placeholder hint text (no
 * "Select an option" / "Search by address or Eircode" / phone example, etc.).
 * Default false → unchanged behaviour for the Advisor accordion and tests.
 */
export const SuppressPlaceholdersContext = React.createContext(false);

/**
 * Maps a field's full `data-testid` → its server validation message (if any).
 * The wizard (`WizardSectionForm`) provides it so every field auto-renders its
 * per-field error (red border + message) with no prop threading — the field's
 * testid mirrors the server's dotted `questionId`. An explicit `error` prop still
 * wins (unit tests / direct callers). Default → no errors.
 */
export const FieldErrorContext = React.createContext<(testId: string) => string | undefined>(
  () => undefined,
);

/**
 * Field ids whose value is currently auto-populated from ANOTHER section (e.g.
 * Expenses "property maintenance" ← Investment Properties, "mortgage expenses" ←
 * property mortgages, …). Inputs in this set render LOCKED + greyed — the same
 * treatment as the Personal Details prefilled identity fields — so the value is
 * edited at its source section, not here. Empty by default → every field stays
 * editable (Advisor accordion, tests, sections with no cross-section derivations).
 */
export const DerivedLockedFieldsContext = React.createContext<ReadonlySet<string>>(new Set());

export type RenderDocumentSlot = (args: {
  field: WizardField;
  value: unknown;
  onChange: (value: unknown) => void;
  testId: string;
}) => React.ReactNode;

/**
 * Renders a field whose options come from a remote catalogue (`field.optionsSource`),
 * e.g. a providers dropdown. Injected by the host app (it owns the HTTP fetch);
 * the shared editor stays transport-agnostic. Return `null`/`undefined` to fall
 * back to the field's normal text/enum rendering.
 */
export type RenderOptionsSelectSlot = (args: {
  field: WizardField;
  value: unknown;
  onChange: (value: unknown) => void;
  testId: string;
}) => React.ReactNode;

export const WIZARD_RADIO_OVERRIDE =
  '[&_input+span]:w-[18px]! [&_input+span]:h-[18px]! [&_input+span]:border-[1.125px]! ' +
  '[&_input+span]:border-purple-primary! [&_input:checked+span]:after:w-[7.875px]! ' +
  '[&_input:checked+span]:after:h-[7.875px]! [&_input+span+span]:text-black! ' +
  '[&_input+span+span]:leading-normal!';

export const WIZARD_SUBLABEL_CLASS =
  'block text-black text-body-02 font-medium! leading-normal font-sans';

/**
 * Standardized per-field validation error message — the SAME token spec as the
 * design-system `.fin-input__error` (TextInput/Select/…): `#96040E`
 * (`--input-error-text`), Body-03 (14px / `--font-weight-regular`), 8px gap
 * (`mt-sm`). Rendered below custom controls that don't use a DS input (money/%/
 * term boxes, phone, radio/multiselect/checkbox groups); DS-backed fields get the
 * identical message from their own `error` prop. Returns null when there's no error.
 */
export const FieldErrorMessage: React.FC<{ testId: string; message?: string }> = ({
  testId,
  message,
}) =>
  message ? (
    <p
      role="alert"
      data-testid={`${testId}-error`}
      className="mt-sm font-sans text-body-03 font-normal leading-normal text-[var(--input-error-text)]"
    >
      {message}
    </p>
  ) : null;

/**
 * Append the standardized error message below a border-less group control
 * (radio / checkbox / multiselect) without disturbing its own layout when there
 * is no error. Returns the control unchanged when `error` is empty so no wrapper
 * div is introduced in the common (no-error) case.
 */
const withFieldError = (
  control: React.ReactElement,
  testId: string,
  error?: string,
): React.ReactElement =>
  error ? (
    <div className="flex flex-col">
      {control}
      <FieldErrorMessage testId={testId} message={error} />
    </div>
  ) : (
    control
  );

interface Props {
  field: WizardField;
  value: unknown;
  onChange: (value: unknown) => void;
  clients: ClientOption[];
  testIdPrefix: string;
  /** Sibling answers of this field's scope — lets `money` resolve `currencyFromField`. */
  groupAnswers?: AnswerMap;
  /** Server-side validation message for this field → red border + message below. */
  error?: string;
  /** Renders `document` fields; omitted → "coming soon" fallback. */
  renderDocument?: RenderDocumentSlot;
  /** Renders fields with `optionsSource` (e.g. a providers dropdown); omitted → plain text/enum. */
  renderOptionsSelect?: RenderOptionsSelectSlot;
}

/**
 * Canonical fallback frequency list for a `money` field that omits its own
 * `frequencies` array. This is the single source of truth shared with the
 * backend: `ProfileFormSectionValidator.DEFAULT_MONEY_FREQUENCIES` mirrors it,
 * so a value the wizard renders/persists is exactly what the server accepts.
 * A field that declares `frequencies: []` opts out of frequency entirely.
 */
const DEFAULT_FREQUENCIES = ['WEEKLY', 'FORTNIGHTLY', 'MONTHLY', 'ANNUALLY'];

/**
 * Preferred default frequency for a `money` field: MONTHLY when the field offers
 * it, else the first option. Order-independent, so the V223 canonical
 * (WEEKLY-first) list still defaults to Monthly. Returns undefined for a field
 * with no frequency selector (`frequencies: []`).
 */
function defaultFrequency(frequencies: string[]): string | undefined {
  if (frequencies.length === 0) return undefined;
  return frequencies.includes('MONTHLY') ? 'MONTHLY' : frequencies[0];
}

/**
 * Identity fields whose values are prefilled one-way from the client's
 * registration record (`client_profiles`) on Personal Details. They are shown
 * locked (disabled + greyed) so the registration details can't be diverged
 * from inside the Fact Find. Gender / marital status are intentionally NOT in
 * this set — they prefill too but stay editable. A schema can also lock any
 * field explicitly via `field.readOnly`.
 */
const READONLY_PREFILL_SOURCES = new Set([
  'client.firstName',
  'client.lastName',
  'client.email',
  'client.phone',
  'client.address',
  'client.dateOfBirth',
]);

function isReadOnlyField(field: WizardField): boolean {
  return (
    field.readOnly === true ||
    (!!field.prefillFrom && READONLY_PREFILL_SOURCES.has(field.prefillFrom))
  );
}

const FREQUENCY_LABELS: Record<string, string> = {
  WEEKLY: 'Weekly',
  FORTNIGHTLY: 'Fortnightly',
  MONTHLY: 'Monthly',
  ANNUALLY: 'Annually',
  QUARTERLY: 'Quarterly',
  ONCE_OFF: 'Once off',
};

function asMap(value: unknown): AnswerMap {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as AnswerMap) : {};
}

function numberOrUndefined(text: string): number | undefined {
  if (text.trim() === '') return undefined;
  const n = Number(text);
  return Number.isNaN(n) ? undefined : n;
}

const PhoneField: React.FC<{
  field: WizardField;
  value: unknown;
  onChange: (value: unknown) => void;
  testId: string;
  disabled?: boolean;
  error?: string;
}> = ({ field, value, onChange, testId, disabled, error }) => {
  const suppressPlaceholders = React.useContext(SuppressPlaceholdersContext);
  const phone = asMap(typeof value === 'string' ? { number: value } : value);
  const [country, setCountry] = useState<string>(DEFAULT_PHONE_COUNTRY);

  const update = (patch: AnswerMap) => {
    const next = { ...phone, ...patch };
    onChange(!next.number ? undefined : next);
  };

  return (
    <div className="flex flex-col gap-[6px]" data-testid={testId}>
      <InputLabel required={!!field.required}>{field.label ?? field.id}</InputLabel>
      <div className="flex gap-2">
        <div data-testid={`${testId}-code`}>
          <PhoneFlagDropdown
            value={country}
            disabled={disabled}
            onSelect={(alpha2, dialCode) => {
              setCountry(alpha2);
              update({ countryCode: `+${dialCode}` });
            }}
          />
        </div>
        <TextInput
          fluid
          className="flex-1"
          error={error}
          placeholder={suppressPlaceholders ? undefined : '+353 85 234 5678'}
          disabled={disabled}
          value={(phone.number as string) ?? ''}
          onChange={(e) => update({ number: e.target.value || undefined })}
          data-testid={`${testId}-number`}
        />
      </div>
    </div>
  );
};

const FieldShell: React.FC<{
  field: WizardField;
  testId: string;
  children: React.ReactNode;
}> = ({ field, testId, children }) => (
  <div className="flex flex-col gap-[6px]">
    <div className="flex items-center justify-between gap-sm">
      <InputLabel required={!!field.required}>{field.label ?? field.id}</InputLabel>
      {field.info && (
        <Tooltip
          content={field.info}
          position="left"
          trigger={
            <span
              data-testid={`${testId}-info`}
              aria-label={`${field.label ?? field.id} information`}
              tabIndex={0}
              className="inline-flex items-center text-black cursor-help shrink-0"
            >
              <AppIcon name="info circle filled" size={20} />
            </span>
          }
        />
      )}
    </div>
    {children}
  </div>
);

const AMOUNT_INPUT_CLASS =
  'min-w-0 flex-1 border-0 bg-transparent text-body-02 text-black outline-none placeholder:text-grey-04 ' +
  'disabled:text-grey-03 disabled:cursor-not-allowed';

/**
 * The amount `<input>` for a `money` field — a text input that shows the value
 * comma-grouped (3,000 not 3000) while emitting a plain number upward. Lives in
 * its own component so the `useGroupedAmount` hook only mounts for grouped
 * (money) fields, never for the plain `term` value box.
 */
const GroupedAmountInput: React.FC<{
  numberValue: unknown;
  onNumberChange: (value: number | undefined) => void;
  testId: string;
  disabled?: boolean;
}> = ({ numberValue, onNumberChange, testId, disabled }) => {
  const grouped = useGroupedAmount(numberValue, onNumberChange);
  return (
    <input {...grouped} disabled={disabled} data-testid={testId} className={AMOUNT_INPUT_CLASS} />
  );
};

/**
 * A number input with an optional trailing unit/period <select> rendered INSIDE
 * the same bordered box (borderless select + custom chevron on the right).
 * Shared by `money` (amount + frequency) and `term` (value + Months/Years) so
 * both read as one field, not a box plus a separate dropdown below it.
 */
const BoxedNumberWithUnit: React.FC<{
  label: string;
  required: boolean;
  testId: string;
  numberValue: unknown;
  /** Persistent left adornment shown inside the box (e.g. the money currency symbol). */
  prefix?: string;
  /** Persistent right adornment shown inside the box (e.g. the percent `%` sign). */
  suffix?: string;
  /** Group the amount with thousands separators (money); off for `term` durations. */
  groupThousands?: boolean;
  onNumberChange: (value: number | undefined) => void;
  numberTestId: string;
  /** Min/max on the plain number input (e.g. percent 0–100); defaults to min 0. */
  numberMin?: number;
  numberMax?: number;
  unitValue?: string;
  unitOptions?: Array<{ value: string; text: string }>;
  onUnitChange?: (value: string) => void;
  unitTestId?: string;
  /** Locked + greyed (e.g. a value auto-populated from another section). */
  disabled?: boolean;
  /** Server validation message → red box border + message below. */
  error?: string;
}> = ({
  label,
  required,
  testId,
  numberValue,
  prefix,
  suffix,
  groupThousands,
  onNumberChange,
  numberTestId,
  numberMin,
  numberMax,
  unitValue,
  unitOptions,
  onUnitChange,
  unitTestId,
  disabled,
  error,
}) => (
  <div className="flex flex-col" data-testid={testId}>
    <div className="flex flex-col gap-[6px]">
      <InputLabel required={required}>{label}</InputLabel>
      <div
        className={`flex items-center h-[48px] gap-sm rounded-lg border px-md ${
          disabled
            ? 'border-grey-05 bg-grey-06'
            : error
              ? 'border-[var(--input-border-error)] bg-white'
              : 'border-grey-01 bg-white focus-within:border-[var(--input-border-active)] focus-within:shadow-[var(--input-focus-ring)]'
        }`}
      >
      {prefix && (
        // Persistent currency symbol — stays visible once the amount is filled
        // (a placeholder would vanish on the first keystroke).
        <span
          aria-hidden
          data-testid={`${numberTestId}-prefix`}
          className={`shrink-0 text-body-02 ${disabled ? 'text-grey-03' : 'text-black'}`}
        >
          {prefix}
        </span>
      )}
      {groupThousands ? (
        <GroupedAmountInput
          numberValue={numberValue}
          onNumberChange={onNumberChange}
          testId={numberTestId}
          disabled={disabled}
        />
      ) : (
        <input
          type="number"
          min={numberMin ?? 0}
          max={numberMax}
          disabled={disabled}
          value={numberValue == null ? '' : String(numberValue)}
          onChange={(e) => onNumberChange(numberOrUndefined(e.target.value))}
          data-testid={numberTestId}
          className={AMOUNT_INPUT_CLASS}
        />
      )}
      {suffix && (
        // Persistent unit suffix inside the box (e.g. `%`), sitting at the right
        // edge after the flex-1 number input.
        <span
          aria-hidden
          data-testid={`${numberTestId}-suffix`}
          className={`shrink-0 text-body-02 ${disabled ? 'text-grey-03' : 'text-black'}`}
        >
          {suffix}
        </span>
      )}
      {unitOptions && unitOptions.length > 0 && onUnitChange && (
        // Borderless in-field unit selector (money frequency / term unit) — the
        // shared Dropdown `field` variant, so it reads as part of this box and is
        // never a native <select>. It renders its own chevron.
        <Dropdown
          variant="field"
          className="shrink-0"
          aria-label={`${label} unit`}
          options={unitOptions}
          value={unitValue ?? unitOptions[0].value}
          onChange={onUnitChange}
          disabled={disabled}
          data-testid={unitTestId}
        />
      )}
      </div>
    </div>
    <FieldErrorMessage testId={testId} message={error} />
  </div>
);

const MoneyField: React.FC<{
  field: WizardField;
  value: unknown;
  onChange: (value: unknown) => void;
  testId: string;
  /** Resolved currency symbol shown inside the amount input. */
  symbol: string;
  /** Locked + greyed (e.g. a value auto-populated from another section). */
  disabled?: boolean;
  /** Server validation message → red box border + message below. */
  error?: string;
}> = ({ field, value, onChange, testId, symbol, disabled, error }) => {
  const money = asMap(value);
  const frequencies = field.frequencies ?? DEFAULT_FREQUENCIES;
  const update = (patch: AnswerMap) => {
    const next = { ...money, ...patch };
    // Persist the *displayed* default frequency (MONTHLY when offered, else the
    // first option) once an amount exists, so the stored answer always matches
    // what the user sees — otherwise a field left at its default reads as
    // frequency-less and is misinterpreted downstream. Fields with no frequency
    // dropdown (frequencies: []) stay single-amount with no frequency.
    if (next.amount !== undefined && !next.frequency && frequencies.length > 0) {
      next.frequency = defaultFrequency(frequencies);
    }
    onChange(next.amount === undefined && !next.frequency ? undefined : next);
  };
  return (
    <BoxedNumberWithUnit
      label={field.label ?? field.id}
      required={!!field.required}
      testId={testId}
      numberValue={money.amount}
      prefix={symbol}
      groupThousands
      disabled={disabled}
      error={error}
      onNumberChange={(v) => update({ amount: v })}
      numberTestId={`${testId}-amount`}
      unitValue={(money.frequency as string) ?? defaultFrequency(frequencies)}
      unitOptions={
        frequencies.length > 0
          ? frequencies.map((f) => ({ value: f, text: FREQUENCY_LABELS[f] ?? f }))
          : undefined
      }
      onUnitChange={(v) => update({ frequency: v })}
      unitTestId={`${testId}-frequency`}
    />
  );
};

/**
 * A standalone `currency` amount field — a labelled text input that shows the
 * value comma-grouped (3,000 not 3000) while emitting a plain number. Its own
 * component so the `useGroupedAmount` hook isn't called from inside the field
 * `switch` (rules of hooks).
 */
const CurrencyField: React.FC<{
  field: WizardField;
  value: unknown;
  onChange: (value: unknown) => void;
  testId: string;
  error?: string;
}> = ({ field, value, onChange, testId, error }) => {
  const suppressPlaceholders = React.useContext(SuppressPlaceholdersContext);
  const grouped = useGroupedAmount(value, onChange);
  return (
    <TextInput
      label={field.label ?? field.id}
      required={!!field.required}
      fluid
      error={error}
      {...grouped}
      placeholder={suppressPlaceholders ? undefined : field.placeholder}
      data-testid={testId}
    />
  );
};

/**
 * A `date` field rendered with `display: 'month'` — a labelled text input
 * masked as "MM/YYYY" with a calendar icon, emitting a full ISO date (day
 * defaulted to 01). Its own component so the `useMonthYearInput` hook isn't
 * called from inside the field `switch` (rules of hooks). The mask is
 * intrinsic to the field (like the `year` type's "YYYY"), so it shows even
 * when generic placeholders are suppressed.
 */
const MonthYearField: React.FC<{
  field: WizardField;
  value: unknown;
  onChange: (value: unknown) => void;
  testId: string;
  error?: string;
}> = ({ field, value, onChange, testId, error }) => {
  const monthYear = useMonthYearInput(value, onChange);
  return (
    <TextInput
      label={field.label ?? field.id}
      required={!!field.required}
      fluid
      error={error}
      {...monthYear}
      placeholder={field.placeholder ?? 'MM/YYYY'}
      iconRight={<AppIcon name="calendar field" size={16} color="var(--color-black)" />}
      data-testid={testId}
    />
  );
};

const WizardFieldInput: React.FC<Props> = ({
  field,
  value,
  onChange,
  clients,
  testIdPrefix,
  groupAnswers,
  error: errorProp,
  renderDocument,
  renderOptionsSelect,
}) => {
  const suppressPlaceholders = React.useContext(SuppressPlaceholdersContext);
  const label = field.label ?? field.id;
  const required = !!field.required;
  const testId = `${testIdPrefix}-${field.id}`;
  // Prefer an explicit prop; otherwise resolve this field's server error from the
  // wizard-provided context by its testid (which mirrors the dotted questionId).
  const errorFromContext = React.useContext(FieldErrorContext);
  const error = errorProp ?? errorFromContext(testId);
  // Locked when the schema marks it read-only/prefilled OR when the host flags it
  // as currently auto-populated from another section (DerivedLockedFieldsContext).
  const lockedFields = React.useContext(DerivedLockedFieldsContext);
  const readOnly = isReadOnlyField(field) || lockedFields.has(field.id);
  // `error` is spread into every DS-backed control (TextInput/TextArea/Select/
  // CountrySelect/…) so it renders the red border + the standardized message
  // (`.fin-input__error`) automatically. Custom controls handle it explicitly below.
  const common = { label, required, fluid: true, error } as const;
  const labelless = { required, fluid: true, error } as const;

  // A field whose options come from a remote catalogue is delegated to the
  // injected slot; without a resolver it falls through to its normal rendering
  // (e.g. a free-text `string`), so the field degrades gracefully.
  if (field.optionsSource && renderOptionsSelect) {
    const rendered = renderOptionsSelect({ field, value, onChange, testId });
    if (rendered != null && rendered !== false) {
      return <>{rendered}</>;
    }
  }

  switch (field.type) {
    case 'text':
      return (
        <TextArea
          {...common}
          disabled={readOnly}
          value={(value as string) ?? ''}
          maxLength={field.maxLength}
          // `rows: 1` renders the textarea at the 48px input min-height (a
          // single-line default), still user-resizable; omitted → browser default.
          rows={field.rows}
          placeholder={suppressPlaceholders ? undefined : field.placeholder}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
        />
      );

    case 'enum': {
      if (field.display === 'radio') {
        // Vertical round-radio group (same look as the boolean gate / employment
        // owner picker) instead of the default dropdown.
        return withFieldError((
          <fieldset className="border-0 p-0 m-0 flex flex-col gap-[6px]" data-testid={testId}>
            <InputLabel required={required}>{label}</InputLabel>
            <div className={`flex flex-col gap-sm ${WIZARD_RADIO_OVERRIDE}`}>
              {(field.options ?? []).map((o) => {
                const v = optionValue(o);
                return (
                  <Radio
                    key={v}
                    label={optionLabel(o)}
                    checked={value === v}
                    onChange={() => onChange(v)}
                    data-testid={`${testId}-${v}`}
                  />
                );
              })}
            </div>
          </fieldset>
        ), testId, error);
      }
      const options: DropdownOption[] = (field.options ?? []).map((o) => ({
        value: optionValue(o),
        text: optionLabel(o),
      }));
      const control = (
        // Shared portalled Dropdown (never a native <select>). Empty state is the
        // placeholder; an optional field is cleared via the clear affordance
        // (clearable) rather than a blank/"—" option row. The client wizard
        // (suppressed) shows a blank trigger and no clear.
        <Dropdown
          {...(field.info ? labelless : common)}
          placeholder={suppressPlaceholders ? '' : required ? 'Select an option' : field.placeholder}
          clearable={!required && !suppressPlaceholders}
          options={options}
          value={(value as string) ?? ''}
          onChange={(v) => onChange(v === '' ? undefined : v)}
          data-testid={testId}
        />
      );
      return field.info ? (
        <FieldShell field={field} testId={testId}>
          {control}
        </FieldShell>
      ) : (
        control
      );
    }

    case 'multiselect': {
      // Tick any number of options (e.g. Benefit in Kind: Company Car +
      // Health Insurance). Stored as a string[] of selected option values. A
      // legacy single enum value (a bare string from before this became a
      // multiselect) is shown as a single ticked option so it isn't silently lost.
      const selected = Array.isArray(value)
        ? (value as string[])
        : value != null && value !== ''
          ? [String(value)]
          : [];
      return withFieldError((
        <fieldset className="border-0 p-0 m-0 flex flex-col gap-[6px]" data-testid={testId}>
          <InputLabel required={required}>{label}</InputLabel>
          <div className="flex flex-col gap-sm">
            {(field.options ?? []).map((o) => {
              const v = optionValue(o);
              const checked = selected.includes(v);
              return (
                <Checkbox
                  key={v}
                  label={optionLabel(o)}
                  checked={checked}
                  onChange={() => {
                    const next = checked ? selected.filter((s) => s !== v) : [...selected, v];
                    onChange(next.length === 0 ? undefined : next);
                  }}
                  data-testid={`${testId}-${v}`}
                />
              );
            })}
          </div>
        </fieldset>
      ), testId, error);
    }

    case 'boolean':
      if (field.display === 'radio') {
        // No/Yes radio pair — same look as the boolean section/group gate
        // (GateControl). Used for in-form yes/no questions (e.g. pensions
        // "Are you currently paying into this pension?").
        return withFieldError((
          <fieldset className="border-0 p-0 m-0" data-testid={testId}>
            <legend className="font-sans text-[15px] font-medium text-black mb-sm p-0">
              {label}
            </legend>
            <div className={`flex flex-col gap-sm ${WIZARD_RADIO_OVERRIDE}`}>
              <Radio
                label="No"
                checked={value === false}
                onChange={() => onChange(false)}
                data-testid={`${testId}-no`}
              />
              <Radio
                label="Yes"
                checked={value === true}
                onChange={() => onChange(true)}
                data-testid={`${testId}-yes`}
              />
            </div>
          </fieldset>
        ), testId, error);
      }
      return withFieldError((
        <Checkbox
          label={label}
          checked={value === true}
          onChange={(e) => onChange(e.target.checked)}
          data-testid={testId}
        />
      ), testId, error);

    case 'integer':
    case 'number':
    case 'decimal':
      return (
        <TextInput
          {...common}
          type="number"
          value={value == null ? '' : String(value)}
          min={field.min}
          max={field.max}
          placeholder={suppressPlaceholders ? undefined : field.placeholder}
          onChange={(e) => onChange(numberOrUndefined(e.target.value))}
          data-testid={testId}
        />
      );

    case 'year':
      // A 4-digit calendar year (YYYY): a text box capped to 4 numeric digits,
      // not a native number spinner (which accepts partial/oversized values like
      // "26" or "20255"). The stored value stays an integer (validated against the
      // field's min/max on save). The "YYYY" mask is intrinsic to the field, so it
      // shows even when generic placeholder hints are suppressed.
      return (
        <TextInput
          {...common}
          type="text"
          inputMode="numeric"
          maxLength={4}
          disabled={readOnly}
          value={value == null ? '' : String(value)}
          placeholder={field.placeholder ?? 'YYYY'}
          onChange={(e) => onChange(numberOrUndefined(e.target.value.replace(/\D/g, '')))}
          data-testid={testId}
        />
      );

    case 'currency':
      // Monetary amount — shown comma-grouped (3,000) via a text input, since a
      // native number input can neither display nor accept the separator.
      return <CurrencyField field={field} value={value} onChange={onChange} testId={testId} error={error} />;

    case 'percent':
      // `%` sits INSIDE the bordered box (right edge), mirroring the money `€`
      // prefix — not as separate text outside the input.
      return (
        <BoxedNumberWithUnit
          label={label}
          required={required}
          testId={`${testId}-box`}
          numberValue={value}
          suffix="%"
          numberMin={field.min}
          numberMax={field.max}
          disabled={readOnly}
          error={error}
          onNumberChange={(v) => onChange(v)}
          numberTestId={testId}
        />
      );

    case 'date':
      if (field.display === 'month') {
        // Month/year-only capture (e.g. "Date employment started") — a masked
        // MM/YYYY text input; the emitted value stays a full ISO date (day 01).
        return <MonthYearField field={field} value={value} onChange={onChange} testId={testId} error={error} />;
      }
      // Standardised date input — same component as the advisor "Add New
      // Relationship" form (label + required + error baked in).
      return (
        <DateTimeInput
          label={label}
          required={required}
          disabled={readOnly}
          error={error}
          value={(value as string) ?? ''}
          onChange={(v) => onChange(v)}
          data-testid={testId}
        />
      );

    case 'money': {
      const symbol = field.currencyFromField
        ? currencySymbol(groupAnswers?.[field.currencyFromField])
        : '€';
      return (
        <MoneyField
          field={field}
          value={value}
          onChange={onChange}
          testId={testId}
          symbol={symbol}
          disabled={readOnly}
          error={error}
        />
      );
    }

    case 'term': {
      const term = asMap(value);
      const update = (patch: AnswerMap) => {
        const next: AnswerMap = { ...term, unit: (term.unit as string) ?? 'MONTHS', ...patch };
        onChange(next.value === undefined ? undefined : next);
      };
      // Same boxed control as `money` — the Months/Years selector sits INSIDE the
      // input box (right-aligned), not as a separate dropdown below it.
      return (
        <BoxedNumberWithUnit
          label={label}
          required={required}
          testId={testId}
          numberValue={term.value}
          disabled={readOnly}
          error={error}
          onNumberChange={(v) => update({ value: v })}
          numberTestId={`${testId}-value`}
          unitValue={(term.unit as string) ?? 'MONTHS'}
          unitOptions={[
            { value: 'MONTHS', text: 'Months' },
            { value: 'YEARS', text: 'Years' },
          ]}
          onUnitChange={(v) => update({ unit: v })}
          unitTestId={`${testId}-unit`}
        />
      );
    }

    case 'phone':
      return (
        <PhoneField
          field={field}
          value={value}
          onChange={onChange}
          testId={testId}
          disabled={readOnly}
          error={error}
        />
      );

    case 'address':
      return (
        <TextInput
          {...common}
          disabled={readOnly}
          value={typeof value === 'string' ? value : ((asMap(value).text as string) ?? '')}
          placeholder={suppressPlaceholders ? undefined : (field.placeholder ?? 'Search by address or Eircode')}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
        />
      );

    case 'country': {
      // Reuses the same country source as the phone input, but lists the
      // country NAME (not the flag). Stored value = the country name string.
      const control = (
        <CountrySelect
          {...(field.info ? labelless : common)}
          // Client wizard: blank when unselected (no "Select a country" prompt).
          placeholder={suppressPlaceholders ? '' : undefined}
          value={(value as string) ?? ''}
          onChange={(v) => onChange(v || undefined)}
          data-testid={testId}
        />
      );
      return field.info ? (
        <FieldShell field={field} testId={testId}>
          {control}
        </FieldShell>
      ) : (
        control
      );
    }

    case 'clientRef': {
      if (field.display === 'radio') {
        // Inline household-member picker (same look as the employment owner
        // picker): a sub-label above a flex-wrap of squircle radios. Single-select
        // — value stays the client id (or 'JOINT' when allowJoint), server
        // contract unchanged.
        return withFieldError((
          <div className="flex flex-col gap-md" data-testid={testId}>
            <span className={WIZARD_SUBLABEL_CLASS}>{label}</span>
            <div className={`flex flex-wrap gap-x-lg gap-y-sm ${WIZARD_RADIO_OVERRIDE}`}>
              {clients.map((c) => (
                <Radio
                  key={c.id}
                  label={c.name}
                  checked={value === c.id}
                  onChange={() => onChange(c.id)}
                  data-testid={`${testId}-${c.id}`}
                />
              ))}
              {/* "Both" only makes sense for a joint relationship (2+ clients);
                  hide it for a solo relationship. */}
              {field.allowJoint && clients.length > 1 && (
                <Radio
                  label="Both"
                  checked={value === 'JOINT'}
                  onChange={() => onChange('JOINT')}
                  data-testid={`${testId}-JOINT`}
                />
              )}
            </div>
          </div>
        ), testId, error);
      }
      // Mirror the enum dropdown: the empty state is the placeholder ("Select an
      // option" when required); an optional field clears via the clear
      // affordance. Never a native <select>.
      const options: DropdownOption[] = [
        ...clients.map((c) => ({ value: c.id, text: c.name })),
        // Jointly-owned option. Value stays 'JOINT' (server contract unchanged);
        // the label reads "Both" per product (the two clients on the profile).
        // Only offered for a joint relationship (2+ clients) — a solo relationship
        // has nothing to own "both" of.
        ...(field.allowJoint && clients.length > 1 ? [{ value: 'JOINT', text: 'Both' }] : []),
      ];
      return (
        <Dropdown
          {...common}
          placeholder={suppressPlaceholders ? '' : required ? 'Select an option' : field.placeholder}
          clearable={!required && !suppressPlaceholders}
          options={options}
          value={(value as string) ?? ''}
          onChange={(v) => onChange(v === '' ? undefined : v)}
          data-testid={testId}
        />
      );
    }

    case 'document':
      if (renderDocument) {
        return <>{renderDocument({ field, value, onChange, testId })}</>;
      }
      return (
        <div data-testid={testId} className="text-body-03 text-[#6b7280]">
          {label} — document upload coming soon.
        </div>
      );

    default:
      return (
        <TextInput
          {...common}
          disabled={readOnly}
          value={(value as string) ?? ''}
          maxLength={field.maxLength}
          placeholder={suppressPlaceholders ? undefined : field.placeholder}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
        />
      );
  }
};

export default WizardFieldInput;
